import React, { useState, useEffect } from 'react';
import { uploadAPK, listAPKFiles, deleteAPK, getLatestAPKDownloadURL, renameAPK } from '@/lib/apkUpload';
import { useAuth } from '@/AuthContext';
import useCoopMemberRole from '@/hooks/useCoopMemberRole';
import { Download, Upload, Trash2, Smartphone, ExternalLink, AlertCircle, Pencil, Check, X } from 'lucide-react';

export default function APKManagement() {
  const { user } = useAuth();
  const { coopRole, isAdmin } = useCoopMemberRole(!!user);
  const [apkFiles, setApkFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [version, setVersion] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [renamingFile, setRenamingFile] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);

  // Check if user is admin using proper role system
  const hasAdminAccess = coopRole === 'admin' || isAdmin();

  useEffect(() => {
    if (hasAdminAccess) {
      loadAPKFiles();
    }
  }, [hasAdminAccess]);

  const loadAPKFiles = async () => {
    try {
      setLoading(true);
      const files = await listAPKFiles();
      setApkFiles(files);
    } catch (err) {
      setError('Failed to load APK files: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.apk')) {
      setSelectedFile(file);
      setError('');
    } else {
      setError('Please select a valid APK file');
      setSelectedFile(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !version.trim()) {
      setError('Please select an APK file and enter a version');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setSuccess('');

      const result = await uploadAPK(selectedFile, version.trim(), (progress) => {
        setUploadProgress(progress);
      });

      setSuccess(`APK uploaded successfully! Version: ${result.version}`);
      setSelectedFile(null);
      setVersion('');
      setUploadProgress(0);
      
      // Reload the list
      await loadAPKFiles();
    } catch (err) {
      setError('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileName) => {
    if (!confirm('Are you sure you want to delete this APK file?')) {
      return;
    }

    try {
      await deleteAPK(fileName);
      setSuccess('APK file deleted successfully');
      await loadAPKFiles();
    } catch (err) {
      setError('Delete failed: ' + err.message);
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    setSuccess('Download URL copied to clipboard!');
  };

  const startRename = (apk) => {
    setRenamingFile(apk.name);
    setRenameValue(apk.name.replace(/\.apk$/i, ''));
    setError('');
    setSuccess('');
  };

  const cancelRename = () => {
    setRenamingFile(null);
    setRenameValue('');
    setRenaming(false);
  };

  const handleRename = async (fileName) => {
    if (!renameValue.trim()) {
      setError('Please provide a new filename');
      return;
    }

    try {
      setRenaming(true);
      setError('');
      setSuccess('');
      await renameAPK(fileName, renameValue.trim());
      setSuccess('APK renamed successfully');
      cancelRename();
      await loadAPKFiles();
    } catch (err) {
      setError('Rename failed: ' + err.message);
      setRenaming(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!hasAdminAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You need admin privileges to access this page.</p>
          <p className="text-sm text-gray-500 mt-2">
            Your role: {coopRole || 'No role assigned'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <Smartphone className="h-6 w-6 text-green-600 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">APK Management</h1>
            </div>
            <p className="text-gray-600 mt-1">Upload and manage Android APK files for public download</p>
          </div>

          <div className="p-6">
            {/* Upload Section */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload New APK</h2>
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      APK File
                    </label>
                    <input
                      type="file"
                      accept=".apk"
                      onChange={handleFileSelect}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Version (e.g., 1.0.0)
                    </label>
                    <input
                      type="text"
                      value={version}
                      onChange={(e) => setVersion(e.target.value)}
                      placeholder="1.0.0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>

                {selectedFile && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-md">
                    <p className="text-sm text-blue-800">
                      Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                    </p>
                  </div>
                )}

                {uploading && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                      <span>Uploading...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleUpload}
                  disabled={!selectedFile || !version.trim() || uploading}
                  className="mt-4 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload APK'}
                </button>
              </div>
            </div>

            {/* Messages */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-800">{error}</p>
              </div>
            )}

            {success && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-green-800">{success}</p>
              </div>
            )}

            {/* APK Files List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Uploaded APK Files</h2>
                <button
                  onClick={loadAPKFiles}
                  disabled={loading}
                  className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
                >
                  {loading ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading APK files...</p>
                </div>
              ) : apkFiles.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Smartphone className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No APK files uploaded yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {apkFiles.map((apk, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <Smartphone className="h-5 w-5 text-green-600 mr-2" />
                            <h3 className="font-medium text-gray-900">{apk.name}</h3>
                            {index === 0 && (
                              <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                                Latest
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            Version: {apk.version} • Uploaded: {new Date().toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => startRename(apk)}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                            title="Rename APK"
                            disabled={renaming && renamingFile === apk.name}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => copyToClipboard(apk.downloadURL)}
                            className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-md"
                            title="Copy download URL"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </button>
                          <a
                            href={apk.downloadURL}
                            download
                            className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-md"
                            title="Download APK"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                          <button
                            onClick={() => handleDelete(apk.name)}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md"
                            title="Delete APK"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {renamingFile === apk.name && (
                        <div className="mt-4 bg-white border border-blue-100 rounded-md p-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            New file name
                          </label>
                          <div className="flex flex-col sm:flex-row gap-3">
                            <input
                              type="text"
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g. release-v1-2-1"
                              disabled={renaming}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleRename(apk.name)}
                                disabled={renaming}
                                className="inline-flex items-center bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Save
                              </button>
                              <button
                                onClick={cancelRename}
                                disabled={renaming}
                                className="inline-flex items-center bg-gray-100 text-gray-700 px-3 py-2 rounded-md hover:bg-gray-200 disabled:opacity-50"
                              >
                                <X className="h-4 w-4 mr-1" />
                                Cancel
                              </button>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            The `.apk` extension will be added automatically if omitted.
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Public Download Instructions */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Public Download</h3>
              <p className="text-blue-800 mb-4">
                APK files are publicly accessible. Anyone can download them using the direct URLs.
              </p>
              <div className="bg-white rounded-md p-3 border border-blue-200">
                <p className="text-sm text-gray-700">
                  <strong>Latest APK URL:</strong> {apkFiles.length > 0 ? apkFiles[0].downloadURL : 'No APK uploaded yet'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
